import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { fetchMarketData, subscribeToMarketUpdates, MarketData } from '../services/marketDataService';

// Fade in animation
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Pulse animation for updates
const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.02); }
  100% { transform: scale(1); }
`;

// Card component for each market data item
const MarketCard = styled.div`
  background: rgba(18, 34, 64, 0.6);
  border-radius: 15px;
  padding: 25px;
  margin: 15px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 194, 255, 0.2);
  flex: 1;
  min-width: 220px;
  animation: ${fadeIn} 0.5s ease-out;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 12px 35px rgba(0, 194, 255, 0.4);
    border-color: rgba(0, 194, 255, 0.5);
  }
  
  @media (max-width: 768px) {
    min-width: 180px;
    padding: 20px;
    margin: 10px;
  }
  
  @media (max-width: 480px) {
    min-width: 150px;
    padding: 15px;
    margin: 8px;
  }
`;

const MarketName = styled.h3`
  margin: 0 0 15px 0;
  font-size: 1.3rem;
  color: #ffffff;
  display: flex;
  align-items: center;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.1rem;
  }
`;

const MarketSymbol = styled.span`
  font-size: 0.9rem;
  color: #64ffda;
  margin-left: 10px;
  background: rgba(0, 194, 255, 0.1);
  padding: 3px 8px;
  border-radius: 10px;
`;

const MarketValue = styled.div`
  font-size: 1.8rem;
  font-weight: 700;
  margin: 15px 0;
  color: #ffffff;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
  
  @media (max-width: 480px) {
    font-size: 1.3rem;
  }
`;

const MarketChange = styled.div<{ positive: boolean }>`
  font-size: 1.1rem;
  color: ${props => props.positive ? '#64ffda' : '#ff6b6b'};
  font-weight: 600;
  display: flex;
  align-items: center;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const MarketGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 25px;
  margin: 30px 0;
  
  @media (max-width: 768px) {
    gap: 20px;
    margin: 25px 0;
  }
  
  @media (max-width: 480px) {
    gap: 15px;
    margin: 20px 0;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 30px;
  font-size: 1.3rem;
  color: #64ffda;
  animation: ${fadeIn} 1s ease-out;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
    padding: 25px;
  }
  
  @media (max-width: 480px) {
    font-size: 1.1rem;
    padding: 20px;
  }
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 30px;
  font-size: 1.3rem;
  color: #ff6b6b;
  background: rgba(255, 107, 107, 0.1);
  border-radius: 12px;
  margin: 30px;
  animation: ${fadeIn} 1s ease-out;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
    padding: 25px;
    margin: 25px;
  }
  
  @media (max-width: 480px) {
    font-size: 1.1rem;
    padding: 20px;
    margin: 20px;
  }
`;

const MarketDataComponent = () => {
  const [marketData, setMarketData] = useState<MarketData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Fetch initial market data
    const loadMarketData = async () => {
      try {
        setLoading(true);
        const data = await fetchMarketData();
        setMarketData(data);
        setLoading(false);
      } catch (err) {
        setError('Failed to load market data. Please try again later.');
        setLoading(false);
        console.error('Error fetching market data:', err);
      }
    };

    loadMarketData();

    // Subscribe to real-time updates
    const unsubscribe = subscribeToMarketUpdates((data) => {
      setMarketData(data);
    });

    // Cleanup subscription on component unmount
    return () => {
      unsubscribe();
    };
  }, []);

  if (loading) {
    return <LoadingMessage>Loading market data...</LoadingMessage>;
  }

  if (error) {
    return <ErrorMessage>{error}</ErrorMessage>;
  }

  return (
    <MarketGrid>
      {marketData.map((item, index) => (
        <MarketCard key={item.id} style={{ animationDelay: `${index * 0.1}s` }}>
          <MarketName>
            {item.name}
            <MarketSymbol>{item.symbol}</MarketSymbol>
          </MarketName>
          <MarketValue>{item.value}</MarketValue>
          <MarketChange positive={item.positive}>
            {item.positive ? '▲' : '▼'} {item.change} ({item.changePercent})
          </MarketChange>
        </MarketCard>
      ))}
    </MarketGrid>
  );
};

export default MarketDataComponent;